import { computeDeepLink } from "@/components/common/deep-link/deep-link-utils";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectActiveAnalysisId } from "@/store/point-cloud-analysis-tool-selector";
import { store } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { getEnvAndRegionFromOrigin } from "@/utils/env-utils";
import { convertToDateString, isLocalhost } from "@faro-lotv/foundation";
import { isIElementGenericDataset } from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectIElement,
  selectProjectName,
} from "@faro-lotv/project-source";
import jsPDF from "jspdf";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * @returns a promise to return the actual width and height of the bitmap image in pixels
 * @param dataUrl Base64 or URL string of the image
 */
function getImageSize(
  dataUrl: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/** Type definition for the data required to generate a PDF report */
export type PdfReportData = {
  /** Base64 or URL string of the image to include in the PDF */
  imageDataUrl: string;

  /** Description or main text content for the report */
  description: string;

  /** Title of the report */
  title: string;

  /** Type of analysis */
  analysisType: string;
};

/**
 *
 * Hook that returns a function to generate and download a PDF report.
 * It defines the structure of the PDF, including a logo, title, project information
 *
 * @returns A callback function that takes an image URL and report text, and generates a PDF report.
 */
export function useSavePdfReport(): (params: PdfReportData) => Promise<void> {
  const viewContext = useViewRuntimeContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const projectName = useAppSelector(selectProjectName);
  const activeAnalysisId = useAppSelector(selectActiveAnalysisId);
  const activeAnalysis = useAppSelector(selectIElement(activeAnalysisId));

  // Ideally, we would report the actual date of capture, but it's not available in the current data structure.
  // The closest available value is the creation date of the DataSet node.
  const dataSetElement = useAppSelector(
    selectAncestor(activeAnalysis, isIElementGenericDataset),
  );

  const region = useMemo(() => {
    let reg = "com";
    const { origin, hostname } = location;
    if (!isLocalhost(hostname)) {
      [, reg] = getEnvAndRegionFromOrigin(origin);
    }
    return reg;
  }, []);

  // Keep latest values in refs so the returned function can be stable
  const projectNameRef = useRef(projectName);
  const activeAnalysisIdRef = useRef(activeAnalysisId);
  const dataSetElementRef = useRef(dataSetElement);
  const viewContextRef = useRef(viewContext);
  const handleErrorWithToastRef = useRef(handleErrorWithToast);
  const regionRef = useRef(region);

  useEffect(() => {
    projectNameRef.current = projectName;
  }, [projectName]);
  useEffect(() => {
    activeAnalysisIdRef.current = activeAnalysisId;
  }, [activeAnalysisId]);
  useEffect(() => {
    dataSetElementRef.current = dataSetElement;
  }, [dataSetElement]);
  useEffect(() => {
    viewContextRef.current = viewContext;
  }, [viewContext]);
  useEffect(() => {
    handleErrorWithToastRef.current = handleErrorWithToast;
  }, [handleErrorWithToast]);
  useEffect(() => {
    regionRef.current = region;
  }, [region]);

  // Stable callable (never re-created) that reads latest values from refs
  return useCallback(
    async ({
      imageDataUrl,
      description,
      title,
      analysisType,
    }: PdfReportData) => {
      const projectName = projectNameRef.current;
      const activeAnalysisId = activeAnalysisIdRef.current;
      const dataSetElement = dataSetElementRef.current;
      const viewContext = viewContextRef.current;
      const region = regionRef.current;

      try {
        const pdf = new jsPDF(
          "p",
          "mm",
          region === "eu" ? "a4" : "letter",
          true,
        );
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Constants (units in mm, as specified above in jsPDF constructor)
        const margin = 15;
        const footerHeight = 10;
        const reservedBottomSpace = margin + footerHeight;
        const titleFontSize = 16;
        const infoFontSize = 11;
        const lineSpacing = 7;
        const sectionSpacing = 10;
        const labelOffsetX = 40;
        const initialY = 10;
        const logoWidth = 28;

        let y = initialY;
        // "helvetica" is default embedded font for this library. Always available.
        const mainReportFont = "helvetica";

        // Logo
        const logoUrl =
          "https://cdn.bfldr.com/4P6TP155/at/2mgt87rmjc8sttbjccc62fkx/Sphere_XG_Logo_-_Color_-_RGB.png?auto=webp&format=png";

        const logoProps = await getImageSize(logoUrl);
        const logoHeight = logoWidth * (logoProps.height / logoProps.width);

        pdf.addImage(
          logoUrl,
          "PNG",
          margin,
          y,
          logoWidth,
          logoHeight,
          undefined,
          "FAST",
        );

        y += logoHeight + sectionSpacing;

        // Title
        pdf.setFontSize(titleFontSize);
        pdf.setFont(mainReportFont, "bold");

        const maxWidth = pageWidth - margin * 2;
        const titleLines = pdf.splitTextToSize(title, maxWidth);
        pdf.text(titleLines, pageWidth / 2, y, { align: "center" });
        y += 0.5 * titleLines.length * titleFontSize;

        // Separator
        const grayColor = 150;
        pdf.setDrawColor(grayColor);
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, pageWidth - margin, y);
        y += sectionSpacing;

        // Project Info
        pdf.setFontSize(infoFontSize);
        pdf.setFont(mainReportFont, "normal");

        const infoRows = [];

        if (projectName) infoRows.push(["Project name:", projectName]);
        infoRows.push([
          "Date of report:",
          convertToDateString(new Date().toISOString()),
        ]);
        if (dataSetElement) {
          infoRows.push([
            "Date of capture:",
            convertToDateString(dataSetElement.createdAt),
          ]);
        }
        infoRows.push(["Analysis type:", analysisType]);

        infoRows.forEach(([label, value]) => {
          pdf.setTextColor(100, 100, 100);
          pdf.text(label, margin, y);
          pdf.setTextColor(0, 0, 0);
          pdf.text(value, margin + labelOffsetX, y);
          y += lineSpacing;
        });

        y += sectionSpacing;

        // Image
        const { width: imgWidth, height: imgHeight } =
          await getImageSize(imageDataUrl);
        const maxImageWidth = pageWidth - 2 * margin;
        let scaledWidth = maxImageWidth;
        let scaledHeight = (imgHeight * scaledWidth) / imgWidth;

        const maxImageBottomY = pageHeight - footerHeight - margin;
        const imageBottomY = y + scaledHeight;

        // If image would overlap the footer
        if (imageBottomY > maxImageBottomY) {
          // Check if it fits on a new page without scaling
          const fitsUnscaled = initialY + scaledHeight <= maxImageBottomY;
          const fitsScaled = pageHeight - 2 * margin - footerHeight;

          if (fitsUnscaled) {
            // Move to next page
            pdf.addPage();
            y = initialY;
          } else {
            // Scale down to fit
            scaledHeight = fitsScaled;
            scaledWidth = (imgWidth * scaledHeight) / imgHeight;

            // Recalculate Y fit
            if (y + scaledHeight > maxImageBottomY) {
              pdf.addPage();
              y = initialY;
            }
          }
        }

        pdf.addImage(imageDataUrl, "PNG", margin, y, scaledWidth, scaledHeight);
        y += scaledHeight + sectionSpacing;

        // Report Text
        const textLines: string[] = pdf.splitTextToSize(
          description,
          maxImageWidth,
        );
        textLines.forEach((line) => {
          if (y > pageHeight - reservedBottomSpace) {
            pdf.addPage();
            y = initialY;
          }
          pdf.text(line, margin, y);
          y += lineSpacing;
        });

        // Deep Link to Analysis
        const analysisUrl = computeDeepLink(
          store.getState(),
          viewContext,
          activeAnalysisId,
        );
        const linkBlueColor = 255;
        const linkOffsetX = 65;
        const linkHeight = 10;

        if (y + linkHeight > pageHeight - reservedBottomSpace) {
          pdf.addPage();
          y = initialY;
        }

        pdf.setTextColor(0, 0, linkBlueColor);
        pdf.textWithLink(
          "View analysis in FARO Sphere XG",
          pageWidth - linkOffsetX,
          y + linkHeight,
          {
            url: analysisUrl.toString(),
          },
        );
        pdf.setTextColor(0, 0, 0);

        // Footer: page numbers
        const pageCount = pdf.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          const footerText = `Page ${i} of ${pageCount}`;
          pdf.setFontSize(10);
          pdf.setTextColor(grayColor);
          pdf.text(footerText, pageWidth / 2, pageHeight - footerHeight, {
            align: "center",
          });
        }

        // Use the title as it for report's filename (jsPDF automatically sanitize the title to make a valid filename)
        pdf.save(`${title}.pdf`);
      } catch (error) {
        const handleErrorWithToast = handleErrorWithToastRef.current;
        handleErrorWithToast({
          title: "Failed to generate PDF",
          error,
        });
      }
    },
    [],
  );
}
